import {
  Program,
  type ApiResponse,
  type ProgramFormData,
  type StudentProgramSkill,
  type StudentProgramCompletionWithProgramName,
  type StudentProgramCompletion,
  type PublicProgramSummary,
  type ProgramTemplateImportBody,
  type ProgramTemplateProviderId,
} from '@court-sync/shared';
import { apiGet, apiPost, apiPut, apiDelete, publicApiGet } from './apiClient';

export const programService = {
  async getPrograms(academyId: string, includeInactive = false): Promise<Program[]> {
    const response = await apiGet<Program[]>('/programs', {
      params: { academyId, includeInactive },
    });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch programs');
  },

  async importProgramTemplate(
    academyId: string,
    body: Omit<ProgramTemplateImportBody, 'academyId'> & { templateId: ProgramTemplateProviderId }
  ): Promise<Program[]> {
    const response = await apiPost<Program[]>('/programs/import-template', {
      academyId,
      templateId: body.templateId,
      stageMappings: body.stageMappings,
    });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to import program template');
  },

  async createProgram(academyId: string, data: ProgramFormData): Promise<Program> {
    const response = await apiPost<Program>('/programs', {
      ...data,
      academyId,
    });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to create program');
  },

  async updateProgram(
    id: string,
    academyId: string,
    data: Partial<ProgramFormData>
  ): Promise<Program> {
    const response = await apiPut<Program>(`/programs/${id}`, {
      ...data,
      academyId,
    });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to update program');
  },

  async deleteProgramPathway(
    rootProgramId: string,
    academyId: string
  ): Promise<{ deletedCount: number }> {
    const response = await apiDelete<{ deletedCount: number }>(
      `/programs/pathway/${encodeURIComponent(rootProgramId)}`,
      { params: { academyId } }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to delete pathway');
  },

  async deleteProgram(id: string, academyId: string): Promise<void> {
    const response = await apiDelete(`/programs/${id}`, {
      params: { academyId },
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete program');
    }
  },

  async getStudentProgramSkills(
    programId: string,
    studentId: string,
    academyId: string
  ): Promise<StudentProgramSkill[]> {
    const response = await apiGet<StudentProgramSkill[]>(
      `/programs/${programId}/students/${studentId}/skills`,
      { params: { academyId } }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch skill progress');
  },

  async updateStudentProgramSkill(
    programId: string,
    studentId: string,
    academyId: string,
    body: { skillName: string; completed: boolean; notes?: string }
  ): Promise<StudentProgramSkill> {
    const response = await apiPut<StudentProgramSkill>(
      `/programs/${programId}/students/${studentId}/skills`,
      { ...body, academyId }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to update skill');
  },

  async getStudentProgramCompletions(
    studentId: string,
    academyId: string
  ): Promise<StudentProgramCompletionWithProgramName[]> {
    const response = await apiGet<StudentProgramCompletionWithProgramName[]>(
      `/programs/students/${studentId}/completions`,
      { params: { academyId } }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch completions');
  },

  async markProgramComplete(
    programId: string,
    studentId: string,
    academyId: string
  ): Promise<StudentProgramCompletion> {
    const response = await apiPost<StudentProgramCompletion>(
      `/programs/${programId}/students/${studentId}/complete`,
      { academyId }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to mark program complete');
  },

  async markCertificateIssued(
    programId: string,
    studentId: string,
    academyId: string
  ): Promise<StudentProgramCompletion> {
    const response = await apiPost<StudentProgramCompletion>(
      `/programs/${programId}/students/${studentId}/certificate`,
      { academyId }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to record certificate');
  },
};

/** Unauthenticated public browse — returns [] when academy tier has no Programs feature */
export async function getPublicPrograms(
  academyId: string
): Promise<ApiResponse<PublicProgramSummary[]>> {
  return publicApiGet<PublicProgramSummary[]>(
    `/public/programs/academy/${encodeURIComponent(academyId)}`
  );
}
